<template>
  <div id="uploadView">
    <form-wizard
      ref="update-view-form-wizard"
      disable-back-navigation
      :start-index.sync="activeTabIndex"
      loading-icon="spinner-border spinner-border-sm"
      :next-button-text="nextButtonText"
      :custom-right-button-text="rightButtonText"
      :loading.sync="loading"
      @on-custom-right="onCustomRight"
      @on-complete="onComplete"
    >
      <component
        :is="tab.component || UpdateQuestion"
        v-for="(tab, index) in filteredTabs"
        :key="index"
        v-bind="tab.props"
      />
    </form-wizard>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import UploadData from '@/components/admin/upload/UploadData.vue'
import UploadShp from '@/components/admin/upload/UploadShp.vue'
import ShpSettings from '@/components/admin/upload/ShpSettings/ShpSettings.vue'
import CSVSettings from '@/components/admin/upload/CSVSettings/CSVSettings.vue'
import UpdateQuestion from '@/components/admin/upload/UpdateQuestion.vue'
import UpdateValidate from '@/components/admin/upload/UpdateValidate.vue'
import {
  postVersionStatus,
  duplicateModel,
  postConfig,
  getConfigsByVersion,
  getNodesByVersion,
  postConfigItem,
  getGeometries,
} from '@/api'
import { AppConfig, AppRanks, VersionConfig, VersionConfigResponse, VersionStatuses } from '@/types'
import {
  ROUTE_BUILDER_PRIMARY,
  ROUTE_SIDEBAR_SETTINGS,
  VIEW_PROJECTS,
  FIELD_MAP_CONFIG_KEY,
  APP_CONFIG_KEY,
  SHOW_MAP_CONFIG_KEY,
} from '@/constants'
import { FeatureCollection } from 'geojson'
import { useUploadStore } from '@/store/admin/upload'

const components = {
  UploadData,
  UploadShp,
  ShpSettings,
  CSVSettings,
  UpdateQuestion,
  UpdateValidate,
}

interface UpdateViewFormWizard extends Vue {
  changeTab(oldIndex: number, newIndex: number): void
}

@Component({ components })
export default class UpdateView extends Vue {
  UpdateQuestion = UpdateQuestion
  loading = false
  activeTabIndex = 0
  shpValidationRequired = false
  upload = useUploadStore()

  mounted () {
    this.upload.resetAllUploadData()
  }

  get showMap () {
    return this.$typedStore.currentRoute.query.showMap === 'true'
  }

  get tabs () {
    return [
      {
        props: {
          title: 'Update Data?',
          label: 'Do you want to use the existing data or new data?',
          'before-change': this.mergeOldConfigs,
        },
        rightText: 'Existing data',
        nextText: 'New data',
        customAction: this.duplicateCSVs,
      },
      { component: UploadData },
      { component: CSVSettings },
      { component: UpdateValidate },
      {
        props: {
          title: 'Update Geometries?',
          label: 'Do you want to use the existing geometries or upload a new shapefile?',
          'before-change': this.shpValidationNotRequired,
        },
        rightText: 'Existing geometries',
        nextText: 'Upload new shapefile',
        customAction: this.duplicateGeometries,
        mapOnly: true,
      },
      { component: UploadShp, mapOnly: true },
      {
        component: ShpSettings,
        props: {
          validateOnly: this.shpValidationRequired,
        },
        mapOnly: true,
      },
      {
        props: {
          title: 'Update Stories?',
          label: 'Do you want to use existing stories?',
        },
        rightText: 'Yes',
        nextText: 'No',
        customAction: this.duplicateStories,
      },
      {
        props: {
          title: 'Update Settings?',
          label: 'Do you want to use existing settings?',
          'before-change': this.finishingTouch,
        },
        rightText: 'Yes',
        nextText: 'No',
        customAction: this.duplicateConfig,
      },
      {
        props: {
          title: 'Submit',
        },
      },
    ]
  }

  get filteredTabs () {
    return this.tabs.filter(tab => tab.mapOnly ? this.showMap : true)
  }

  get from () {
    const { fromVersionId } = this.$router.currentRoute.query
    const from = Array.isArray(fromVersionId) ? fromVersionId[0] : fromVersionId
    if (from) return Number(from)
    else throw new Error('No fromVersionId')
  }

  get to () {
    return Number(this.$router.currentRoute.params.primaryVersionId)
  }

  get currentTab () {
    return this.filteredTabs[this.activeTabIndex]
  }

  get nextButtonText () {
    return this.loading ? 'Processing' : this.currentTab.nextText || 'Next'
  }

  get rightButtonText () {
    return this.loading ? '' : this.currentTab.rightText
  }

  async onComplete () {
    // always duplicate the custom tabs
    await this.duplicate('custom-tabs')

    await this.postShowMapConfig()

    // Persist loading=true state so finish button remains disabled
    this.loading = true
    // Change version status to built
    await postVersionStatus(VersionStatuses.BUILT)
    // Set loaded to false to force download latest data
    this.$typedStore.primary.app.setAppLoaded(false)
    // Navigate to the builder view to see the uploaded app
    this.$router.push({
      name: ROUTE_BUILDER_PRIMARY,
      params: {
        sidepanel: ROUTE_SIDEBAR_SETTINGS,
        mainpanel: VIEW_PROJECTS,
      },
    })
  }

  incrementTab (increment: number) {
    (this.$refs['update-view-form-wizard'] as UpdateViewFormWizard).changeTab(this.activeTabIndex, this.activeTabIndex + increment)
  }

  async mergeOldConfigs () {
    const configs = await getConfigsByVersion({ versionId: this.from })
    this.upload.setFieldMap(configs.fieldMap)

    // Duplicate source version geom mapping settings
    await this.duplicateGeomSettings(configs.appConfig)

    this.shpValidationRequired = true
    return true
  }

  async duplicateGeomSettings (appConfig: AppConfig | undefined) {
    if (appConfig?.geomColumn && appConfig?.geomShpProp) {
      await this.$typedStore.primary.app.getConfig()
      this.$typedStore.primary.app.updateAppConfig({ prop: 'geomColumn', value: appConfig.geomColumn })
      this.$typedStore.primary.app.updateAppConfig({ prop: 'geomShpProp', value: appConfig.geomShpProp })
      await this.$typedStore.primary.app.saveConfig()
    }
  }

  async duplicateCSVs () {
    const records = await this.duplicate<{ [column: string]: string }[]>('records')
    await Promise.all([
      this.duplicate('config', { key: FIELD_MAP_CONFIG_KEY }),
      this.duplicate('nodes', { duplicate_stories: false, duplicate_config: false }),
      this.showMap ? this.duplicateAppConfigShapeProperties() : Promise.resolve(),
    ])
    // Fetching raw CSV data to handle update shapefile when data is duplicated
    // There's redundancy in this process if the user wants to duplicateShp instead
    this.upload.setRows(records)
    await this.$typedStore.primary.app.getConfig()
    this.incrementTab(4)
  }

  async duplicateAppConfigShapeProperties () {
    const oldConfigs: VersionConfig | VersionConfigResponse = await getConfigsByVersion({ versionId: this.from })
    if (oldConfigs.appConfig) {
      return postConfig({
        key: APP_CONFIG_KEY,
        value: {
          geomColumn: oldConfigs.appConfig.geomColumn,
          geomShpProp: oldConfigs.appConfig.geomShpProp,
        },
      })
    }
  }

  async postShowMapConfig () {
    return postConfig({
      key: SHOW_MAP_CONFIG_KEY,
      value: this.showMap,
    })
  }

  async duplicateGeometries () {
    await Promise.all([
      this.duplicate('geometries'),
    ])

    if (this.shpValidationRequired) {
      // Get the geometries to validate against (with fake geom data to be faster)
      const geojsonString = await getGeometries({ fake: true }, AppRanks.PRIMARY)
      const geojson: FeatureCollection = JSON.parse(geojsonString)
      // Make userproperties be properties
      geojson.features.forEach((feature) => {
        if (feature.properties) feature.properties = { ...feature.properties.userproperties }
      })
      this.upload.setGeom(geojson)
      this.incrementTab(2)
    } else {
      this.incrementTab(3)
    }
  }

  async duplicateStories () {
    await this.duplicate('stories')
    this.incrementTab(1)
  }

  async duplicateConfig () {
    // Get configs that need to be duplicated
    const [oldConfigs, oldNodes] = await Promise.all([
      getConfigsByVersion({ versionId: this.from }),
      // getConfigsByVersion({ versionId: this.to }),
      getNodesByVersion({ versionId: this.from }),
    ])
    await Promise.all([
      ...oldNodes.map((node) => {
        if (node?.config) {
          return postConfigItem(node.path, 'config', node.config)
        }
      }),
      ...Object.entries(oldConfigs)
        .filter(([key]) => ![APP_CONFIG_KEY, FIELD_MAP_CONFIG_KEY].includes(key))
        .map(([key, value]) => {
          return postConfig({ key: key as keyof VersionConfig | keyof VersionConfigResponse, value })
        }),
      this.duplicateAppConfigWithNewGeomSettings(oldConfigs?.appConfig),
    ])
    this.incrementTab(1)
  }

  async duplicateAppConfigWithNewGeomSettings (appConfig: AppConfig | undefined) {
    if (appConfig) {
      appConfig.geomColumn = this.$typedStore.primary.app.config.appConfig.geomColumn
      appConfig.geomShpProp = this.$typedStore.primary.app.config.appConfig.geomShpProp

      return postConfig({
        key: APP_CONFIG_KEY,
        value: appConfig,
      })
    }

    return Promise.resolve('Success')
  }

  async onCustomRight () {
    // called when the right-hand custom button is pushed
    this.loading = true
    await this.currentTab.customAction?.()
    this.loading = false
  }

  async duplicate<T = unknown> (modelName: string, body?: object) {
    return duplicateModel<T>(modelName, this.from, this.to, body || {})
  }

  shpValidationNotRequired () {
    this.shpValidationRequired = false
    return true
  }

  async finishingTouch () {
    // Following codes below taken from CSVSettings
    // Fetch the data and populate Vuex
    // addConfigToNodes depends on treedata being populated
    await this.$typedStore.visualisationData.getTreeData()
    await this.$typedStore.primary.app.getConfig()
    await this.upload.addConfigToNodes()
    return true
  }
}

</script>
